//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ### STYLES ###
import { useTheme } from '@mui/material/styles';
import { Box, TextField, Button, Link, InputAdornment, IconButton  } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

// === API ===
import { resetPassword } from 'api/prokey/prokey.axios';

//#endregion

//-----------------------------------------------------------------
export function PassResetForm() {
  //-----------------------------------------------------------------
  
  //#region [VALUES]

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [password_strength_style, setPassStrengthClass] = useState([]);
  const [alert_info , setAlertInfo] = useState({ style: [], message: '' });

  const error_message_default = 'Unable to reset password';
  const passRegexStrong = new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})');
  const passRegexMedium = new RegExp(
    '((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{6,}))'
  );

  const reset_key = new URLSearchParams(location.search).get('key');

  //#endregion

  //#region [STYLES]

  const _styles = {
    alert: {
      display          : 'inline-block',
      color            : 'white',
      marginTop        : '1rem',
      padding          : '1rem',
      width            : '100%',
      borderRadius     : '4px',
    },
    hidden: {
      display: 'none',
    },
    failure: {
      backgroundColor: 'darkred',
    },
    success: {
      backgroundColor: 'darkgreen',
    },

    password: {
      width  : '100%'   ,
      margin : '0.5rem' ,
    },
    password_strength: {      
      width   : '100%'   ,
      height  : '0.5rem' ,
      margin  : '0.5rem' ,
      padding : '0'      ,
      backgroundColor: 'rgba(  0,   0, 0, 0.3 )',
    },
    strong: {
      backgroundColor: 'rgba(  60, 255, 0, 0.6 )',
    },
    medium: {
      backgroundColor: 'rgba( 255, 251, 0, 0.6 )',
    },
    weaker: {
      backgroundColor: 'rgba( 255,   0, 0, 0.6 )',
    },

    RESET_PASSWORD_BUTTON: {
      marginLeft: '1rem',
    }
  }


  const _props = {
    ALERT: {
      sx      :  alert_info.style    ,
      children: [alert_info.message] ,
    },

    PASSWORD_1: {
        name       : 'password1_txt',
        type       : showPassword ? 'test' : 'password',
        variant    : 'filled'         ,
        sx         : _styles.password ,
        label      : 'new password'   ,
        value      : password1        ,
        onFocus    : clearAlert       ,
        onKeyPress : handleKeyPress   ,
        onChange   : handlePassword1Change,
        InputProps : { 
          endAdornment: displayPasswordVisiblityButton(),
        }
    },
    PASSWORD_2: {
        name       : 'password2_txt',
        type       : showPassword ? 'test' : 'password',
        variant    : 'filled'         ,
        sx         : _styles.password ,
        label      : 'retype new password'   ,
        value      : password2        ,
        onFocus    : clearAlert       ,
        onKeyPress : handleKeyPress   ,
        onChange   : handlePassword2Change,
        InputProps : { 
          endAdornment: displayPasswordVisiblityButton(),
        }
    },

    GO_BACK_LINK: {
      name      : 'go_back_link',
      component : 'button',
      onClick   : handleGoBack,
      children  : ['Back to login'],
    },
    RESET_PASSWORD_BUTTON: {
      name      : 'reset_password_btn',
      sx       : _styles.RESET_PASSWORD_BUTTON,
      variant  : 'contained' ,
      color    : 'primary'   ,
      onClick  : putLogin    ,
      children : ['RESET']   ,
    }
  }


  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  async function putLogin() {
    //-----------------------------------------------------------------
    try {
      clearAlert();

      const mismatch = password1 != password2;
      if( mismatch ) {
        setAlertInfo({ style: [_styles.alert, _styles.failure], message: 'Passwords do not match' });
        return;
      }

      const too_weak = !passRegexStrong.test(password1);
      if( too_weak ) {
        setAlertInfo({ style: [_styles.alert, _styles.failure], message: 'Passwords is not strong enough' });
        return;
      }

      const result = await resetPassword({ oneTimeKey: reset_key, newPassword: password1 });
      if( result.status !== 200 ) throw result.status;

      // Reset Success, provide feedback!!!
      setAlertInfo({ style: [_styles.alert, _styles.success], message: 'Password successfully changed!' });
      setTimeout(handleGoBack, 3000);
    } catch (error) {
      setAlertInfo( { style: [_styles.alert, _styles.failure], message: error_message_default } );
    }
  }

  //-----------------------------------------------------------------
  function handlePassword1Change(event) {
    //-----------------------------------------------------------------    
    setPassword1(event.target.value);
    updatePasswordStrength(event.target.value);
  }

  //-----------------------------------------------------------------
  function handlePassword2Change(event) {
    //-----------------------------------------------------------------    
    setPassword2(event.target.value);
  }

  //-----------------------------------------------------------------
  function handleKeyPress(target) {
    //-----------------------------------------------------------------
    if (target.charCode === 13) putLogin();
  }

  //-----------------------------------------------------------------
  function handleGoBack() {
    //-----------------------------------------------------------------
    navigate( '../' );
  }

  //-----------------------------------------------------------------
  function toggleShowPassword() {
    //-----------------------------------------------------------------
    setShowPassword(!showPassword);
  };

  //-----------------------------------------------------------------
  function handleMouseDownPassword(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
  };

  //-----------------------------------------------------------------
  function clearAlert() {
    //-----------------------------------------------------------------
    setAlertInfo( {style: [_styles.hidden], message: ''} );
  }

  //-----------------------------------------------------------------
  function updatePasswordStrength(pass) {
    //-----------------------------------------------------------------
         if ( !pass || pass === ''       ) setPassStrengthClass( [ _styles.password_strength                 ] );
    else if ( passRegexStrong.test(pass) ) setPassStrengthClass( [ _styles.password_strength, _styles.strong ] );
    else if ( passRegexMedium.test(pass) ) setPassStrengthClass( [ _styles.password_strength, _styles.medium ] );
    else                                   setPassStrengthClass( [ _styles.password_strength, _styles.weaker ] );
  }

  //#endregion


  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (      
      <Box sx={ _styles.container }>

        <TextField {..._props.PASSWORD_1} />
        <Box sx={password_strength_style} />
        <TextField {..._props.PASSWORD_2} />

        <Box sx={ _styles.sign_in_container }>
          <Link {..._props.GO_BACK_LINK}  />
          <Button {..._props.RESET_PASSWORD_BUTTON } />
        </Box>
        <Box {..._props.ALERT}/>
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displayPasswordVisiblityButton() {
    //-----------------------------------------------------------------
    return (
      <InputAdornment position='end'>
        <IconButton
          aria-label  = 'toggle password visibility'
          onClick     = {toggleShowPassword}
          onMouseDown = {handleMouseDownPassword}
          edge        = 'end'
        >
          {showPassword ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </InputAdornment>
    )
  }

  //#endregion

  return display();
}